import { OnboardingProcessStepNameType } from 'api/types/entity';
import { LoadingLayout } from 'components';
import { config } from 'config';
import { useInterval } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { usePageTitle } from 'libs/navigation';
import { OnboardingLayout } from 'modules/onboarding/common/components';
import { STATUS_UPDATE_INTERVAL } from 'modules/onboarding/common/constants';
import { OnboardingProvider } from 'modules/onboarding/common/providers';

import { WaitingParticipantsKyc } from '../components/WaitingParticipantsKyc';
import { LegalClientOnboardingStatusFeature } from '../feature';
import { getInnerComponent } from '../helpers/getInnerComponent';
import { useLegalClientOnboardingStatus } from '../hooks';
import { useIsWaitingOnboardingStep } from '../hooks/useIsWaitingOnboardingStep';
import { useOnboarding } from '../hooks/useOnboarding';
import { useParticipantsWaitingKyc } from '../hooks/useParticipantsWaitingKyc';
import { pagesMap } from '../route';

interface Props {
  clientId: string;
}

export const LegalContainer = ({ clientId }: Props) => {
  const { t } = useTranslation();

  usePageTitle(t('onboarding.pageTitle'));

  const {
    currentClient,
    legalClient,
    loading,
    innerState,
    process,
    refetchCurrentClient,
    refetchLegalClient,
    onGoBack,
  } = useOnboarding(clientId);

  const {
    isWaitingKyc,
    users,
    formErrorSaveEmail,
    loadingSaveEmail,
    onEditEmail,
    onResendCode,
    getLegalClientParticipants,
  } = useParticipantsWaitingKyc(currentClient, legalClient);

  const onboardingStatus = useLegalClientOnboardingStatus(
    legalClient,
    currentClient
  );

  const isWaitingOnboardingStep = useIsWaitingOnboardingStep(
    process?.currentStep.name,
    legalClient?.status
  );

  useInterval(
    () => {
      refetchCurrentClient();
      refetchLegalClient();
      if (legalClient && isWaitingKyc) {
        getLegalClientParticipants(legalClient.id);
      }
    },
    onboardingStatus || isWaitingKyc ? STATUS_UPDATE_INTERVAL : null
  );

  if (!currentClient || loading) {
    return <LoadingLayout />;
  }

  if (onboardingStatus) {
    return (
      <LegalClientOnboardingStatusFeature onboardingStatus={onboardingStatus} />
    );
  }

  if (isWaitingKyc) {
    return (
      <WaitingParticipantsKyc
        email={config.EMAIL_ONBOARDING ?? ''}
        initialEmailEditErrors={formErrorSaveEmail}
        loadingEmailEdit={loadingSaveEmail}
        users={users}
        onEditEmail={onEditEmail}
        onResend={onResendCode}
      />
    );
  }

  if (isWaitingOnboardingStep) {
    return <LoadingLayout />;
  }

  return (
    <OnboardingProvider
      clientId={currentClient.id}
      data={{ currentClient, legalClient, innerState, refetchLegalClient }}
      pagesMap={pagesMap}
      process={process!}
      renderChild={({ route }) => {
        const InnerComponent = getInnerComponent({
          currentClient,
          legalClient,
          innerState,
          currentPageName: route.pageKey as OnboardingProcessStepNameType,
        });

        if (InnerComponent) {
          return InnerComponent;
        }
        const Component = route.page.component;
        return (
          <OnboardingLayout
            key={route.pageKey}
            countSteps={process?.totalStepCount}
            currentStepIndex={route.pageNumber}
          >
            <Component {...route.page.componentProps} />
          </OnboardingLayout>
        );
      }}
      onGoBack={onGoBack}
    />
  );
};
